import { postResponse, useMatchScreen } from '../context/MatchScreenContext'

interface prefilledFields {
  firstName: string
  lastName: string
  fullName: string
  email: string
  phoneNumber: string
}

/**
 * Extracts and formats user information from match screen responses.
 *
 * Processes response data to create a structured object with user details.
 * Phone numbers are normalized to 10-digit format without country code.
 *
 * @param responses - Array of user responses from the match screen
 * @returns Object containing formatted user information
 */
const createPrefilledFields = (responses: postResponse[]): prefilledFields => {
  let firstName = ''; let lastName = ''; let fullName = ''; let email = ''; let phoneNumber = ''

  responses.forEach((response) => {
    switch (response.questionName) {
      case 'first_name':
        firstName = response.answer || ''
        break
      case 'last_name':
        lastName = response.answer || ''
        break
      case 'email':
        email = response.answer || ''
        break
      case 'phone': {
        // Remove all non-digit characters from phone number
        const cleanedUpPhoneNumber = (response.answer || '').replace(/\D/g, '')
        // Use only the last 10 digits, no country code
        if (cleanedUpPhoneNumber.length === 10) {
          phoneNumber = cleanedUpPhoneNumber
        } else if (cleanedUpPhoneNumber.length === 11 && cleanedUpPhoneNumber.charAt(0) === '1') {
          phoneNumber = cleanedUpPhoneNumber.slice(1) // Remove leading '1'
        } else if (cleanedUpPhoneNumber.length > 10) {
          phoneNumber = cleanedUpPhoneNumber.slice(-10) // Take last 10 digits
        }
        // If phone is not exactly 10 digits after normalization, leave empty
        break
      }
    }
  })

  // Construct full name from available parts
  if (firstName && lastName) {
    fullName = `${firstName} + ${lastName}`
  } else if (firstName) {
    fullName = firstName
  } else if (lastName) {
    fullName = lastName
  }

  return {
    firstName,
    lastName,
    fullName,
    email,
    phoneNumber
  }
}

/**
 * Adds user data as search parameters to Facet revisit links.
 *
 * Uses DOM parsing to find and modify anchor tags containing "facet.com/introductory-call".
 * This approach automatically handles HTML entity decoding (&#61;, &amp;, etc.) from TinyMCE.
 *
 * Appends the following fields as URL search parameters:
 * - email -> user's email address
 * - phone -> user's phone number (10 digits, no country code)
 * - first_name -> user's first name
 * - last_name -> user's last name
 *
 * @param prefilledFields - Object containing user's personal information
 * @param disclosureMarkup - HTML markup string that may contain links
 * @returns The markup with user data added as search parameters to Facet links
 */
const populateFields = (prefilledFields: prefilledFields, disclosureMarkup: string) => {
  if (typeof document === 'undefined') {
    return disclosureMarkup
  }

  // Create a detached DOM container for parsing HTML
  const container = document.createElement('div')
  container.innerHTML = disclosureMarkup

  // Find all anchor tags with href attributes
  const links = container.querySelectorAll('a[href]')

  links.forEach((link) => {
    const href = link.getAttribute('href')
    if (!href) return

    // Check if this is a Facet introductory-call link (case-insensitive)
    if (!href.toLowerCase().includes('facet.com/introductory-call')) return

    try {
      // Parse the URL - browser has already decoded HTML entities
      // Use base URL for protocol-relative URLs (e.g., //facet.com/...)
      const url = new URL(href, 'https://facet.com')

      // Add user data as search parameters
      url.searchParams.set('email', prefilledFields.email)
      url.searchParams.set('phone', prefilledFields.phoneNumber)
      url.searchParams.set('first_name', prefilledFields.firstName)
      url.searchParams.set('last_name', prefilledFields.lastName)

      // Update the link with the modified URL
      link.setAttribute('href', url.toString())
    } catch (error) {
      // If URL parsing fails, leave the link unchanged

    }
  })

  // Return the modified HTML
  return container.innerHTML
}

/**
 * Hook that processes disclosure markup to populate Facet revisit links with user data.
 *
 * This hook extracts user information from stored match screen answers and appends it
 * as search parameters to Facet appointment scheduling links. This allows users to
 * click through to Facet with their information pre-filled.
 *
 * @param disclosureMarkup - HTML markup string containing links to be processed (can be null)
 * @returns The processed markup with user data added as search params to Facet links, or null if no markup provided
 *
 * @example
 * ```tsx
 * const disclosure = `
 *   <a href="https://facet.com/introductory-call">
 *     Schedule appointment
 *   </a>
 * `;
 * const processedDisclosure = useFacetRevisitLink(disclosure);
 * // Result: href becomes "https://facet.com/introductory-call?email=user@example.com&phone=5551234567&first_name=John&last_name=Doe"
 * ```
 *
 * Search parameters added:
 * - email - User's email address
 * - phone - User's phone number (10 digits, no country code)
 * - first_name - User's first name
 * - last_name - User's last name
 */
const useFacetRevisitLink = (disclosureMarkup: string | null): string | null => {
  const { state: { storedAnswers } } = useMatchScreen()
  if (!disclosureMarkup) {
    return null
  }
  const prefilledFields = createPrefilledFields(storedAnswers)
  return populateFields(prefilledFields, disclosureMarkup)
}

export default useFacetRevisitLink
